import '../../css/GenericBuilder/dropHintModal.css';
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { GlobalContext } from '../utils/GlobalContext';
import { CONSTANT } from '../utils/constants';
import CaptchaInput from '../utils/CaptchaInput';

export const DropHint = ({toggleDropHintModal, className = ''}) => {
  const [ formData, setFormData ] = useState({
    friend_email_1: "",
    friend_email_2: "",
    friend_email_3: "",
    friend_email_4: "",
    comments: "",
    your_email: "",
    account_number:"",
  });
  const [captchaData, setCaptchaData] = useState({ value: '', token: '', expires_at: 0 });
  const [captchaRefreshTrigger, setCaptchaRefreshTrigger] = useState(0);
  const [captchaError, setCaptchaError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { client_code, selectedItemId, category, layerparam } = useContext(GlobalContext);
  const themeColor = Array.isArray(layerparam['Color Code']?.color_code)
    ? layerparam['Color Code'].color_code[0]
    : (layerparam['Color Code']?.color_code || '#1e293b');
  const [toast, setToast] = useState({ message: '', type: '' });

  const showToast = (message, type) => setToast({ message, type });

  useEffect(() => {
    if (!toast.message) return;
    const timer = setTimeout(() => {
      setToast({ message: '', type: '' });
      if (toast.type === 'success') toggleDropHintModal();
    }, 3000);
    return () => clearTimeout(timer);
  }, [toast.message, toast.type]);

  const reloadCaptcha = () => {
    setCaptchaData({ value: '', token: '', expires_at: 0 });
    setCaptchaRefreshTrigger(n => n + 1);
    setCaptchaError('');
  };

  const verifyCaptcha = async () => {
    try {
      const response = await axios.post(`${CONSTANT.BASE_URL}api/Builder/verifyCaptcha`, {
        answer: captchaData.value,
        token: captchaData.token,
        expires_at: captchaData.expires_at,
      });
      return response.data.success === true;
    } catch (err) {
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaData.token) {
      setCaptchaError("Please complete the captcha verification.");
      return;
    }

    const isCaptchaValid = await verifyCaptcha();
    if (!isCaptchaValid) {
      setCaptchaError("Incorrect answer. Please try again.");
      reloadCaptcha();
      return;
    }
    setCaptchaError('');

    setIsLoading(true);
    const base_url = `${CONSTANT.BASE_URL}api/Builder/send-drop-hit-email`;
    try {
      const response = await axios.post(base_url, {
        friends_email_first: formData.friend_email_1,
        friends_email_second: formData.friend_email_2,
        friends_email_third: formData.friend_email_3,
        friends_email_fourth: formData.friend_email_4,
        comments: formData.comments,
        your_email: formData.your_email,
        account_number: client_code,
        item_details: selectedItemId?.['itemDetails'],
        selectedItemId: selectedItemId,
        category: category,
      });

      if (response.data['Condition']) {
        handleReset();
        showToast("Your hint has been sent successfully!", 'success');
      } else {
        showToast("Error sending the email. Please contact the admin team.", 'error');
        reloadCaptcha();
      }
    } catch (error) {
      console.error("Error submitting drop hint:", error);
      showToast("Error submitting the form. Please try again.", 'error');
      reloadCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      friend_email_1: "",
      friend_email_2: "",
      friend_email_3: "",
      friend_email_4: "",
      comments: "",
      captcha: "",
      your_email: "",
      account_number: client_code,
    });
    setCaptchaError(false);
  };

  const closeDropHintModal = () => {
    toggleDropHintModal();
  };

  return (
    <>
      {toast.message && (
        <div className={`dh-toast dh-toast--${toast.type}`}>
          <span className="dh-toast__icon">{toast.type === 'success' ? '✔' : '✖'}</span>
          <span className="dh-toast__text">{toast.message}</span>
          <button onClick={() => setToast({ message: '', type: '' })} className="dh-toast__close">×</button>
          {toast.type === 'success' && <div className="dh-toast__bar" />}
        </div>
      )}

      <div className={`modalDropHint${className ? ` ${className}` : ''}`}>
        <div className="modal-content">

          {/* Header */}
          <div className="dh-header" style={{ background: themeColor }}>
            <div className="dh-header__left">
              <div className="dh-header__icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </div>
              <div>
                <h4 className="dh-header__title">Send to a friend</h4>
                <p className="dh-header__subtitle">Share with up to four people</p>
              </div>
            </div>
            <button onClick={closeDropHintModal} className="dh-header__close">×</button>
          </div>

          {/* Body */}
          <div className="dh-body">
            <form onSubmit={handleSubmit} autoComplete="off">
              <input type="text" name="item_id" value={client_code} hidden readOnly />

              {/* Friends Emails */}
              <div className="dh-section">
                <label className="dh-label">Friends' Emails</label>
                {[1, 2, 3, 4].map(n => (
                  <div key={n} className="dh-email-row">
                    <div className="dh-badge">{n}</div>
                    <input
                      className="form-control dh-input"
                      type="email"
                      name={`friend_email_${n}`}
                      placeholder="Friend's email"
                      value={formData[`friend_email_${n}`]}
                      onChange={handleChange}
                      required={n === 1}
                    />
                  </div>
                ))}
              </div>

              {/* Comments */}
              <div className="dh-section">
                <label className="dh-label">Comments</label>
                <textarea
                  className="form-control dh-textarea"
                  name="comments"
                  placeholder="Write a short message..."
                  rows="2"
                  value={formData.comments}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Captcha */}
              <div className="dh-section">
                <CaptchaInput
                  value={captchaData.value}
                  onCaptchaChange={(data) => setCaptchaData(data)}
                  onChange={(e) => setCaptchaData(prev => ({ ...prev, value: e.target.value }))}
                  error={captchaError}
                  refreshTrigger={captchaRefreshTrigger}
                  placeholder="Solve the question above"
                />
              </div>

              {/* Your Email */}
              <div className="dh-section">
                <input
                  className="form-control dh-input"
                  type="email"
                  name="your_email"
                  placeholder="Your email"
                  value={formData.your_email}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Buttons */}
              <div className="dh-btn-row">
                <button type="submit" disabled={isLoading} className="dh-btn-submit">
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm dh-spinner" role="status" aria-hidden="true"></span>
                      Sending...
                    </>
                  ) : 'Submit'}
                </button>
                <button type="button" onClick={handleReset} disabled={isLoading} className="dh-btn-reset">
                  Reset
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </>
  );
};
